/**
 * BoxplotChart.jsx — Variabilité quotidienne des PM (boxplot par jour)
 * Suggestion Dr. Emmanuelle Van Erck (3 avril 2026).
 * Affiché uniquement si la session couvre >= 3 jours calendaires.
 */
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../constants/theme';
import {
  Chart as ChartJS, CategoryScale, LinearScale, Tooltip, Legend, Title
} from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';

ChartJS.register(BoxPlotController, BoxAndWiskers, CategoryScale, LinearScale, Tooltip, Legend, Title, zoomPlugin);

// Clé 'JJ/MM' (fuseau du navigateur, D201) = CLÉ TECHNIQUE de bucketing + tri (sortDayKeys,
// split('/')), PAS un libellé localisable : NON traduite (i18n-owner O3). DD/MM est
// identique fr-FR / en-GB, donc l'axe (labels = clés triées) reste correct dans les 2 langues.
function groupByDay(readings) {
  const days = {};
  readings.forEach(r => {
    if (!r.timestamp) return;
    const key = new Date(r.timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit'
    });
    if (!days[key]) days[key] = [];
    days[key].push(r);
  });
  return days;
}

function sortDayKeys(keys) {
  return [...keys].sort((a, b) => {
    const [da, ma] = a.split('/').map(Number);
    const [db, mb] = b.split('/').map(Number);
    return ma !== mb ? ma - mb : da - db;
  });
}

const BoxplotChart = ({ readings, darkMode = false }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);

  const c = {
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    textSec: darkMode ? '#B8BFC7' : '#6B7280',
    card:    darkMode ? '#1E1E32' : '#fff',
    border:  darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    grid:    darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  };

  const { labels, pm25Data, pm10Data, nbDays } = useMemo(() => {
    if (!readings || readings.length < 2) return { labels: [], pm25Data: [], pm10Data: [], nbDays: 0 };
    const byDay = groupByDay(readings);
    const sorted = sortDayKeys(Object.keys(byDay));
    return {
      labels:   sorted,
      pm25Data: sorted.map(d => byDay[d].map(r => r.pm2_5).filter(v => v != null)),
      pm10Data: sorted.map(d => byDay[d].map(r => r.pm10).filter(v => v != null)),
      nbDays:   sorted.length,
    };
  }, [readings]);

  if (nbDays < 3) return (
    <div style={{ background: c.card, borderRadius: '16px', padding: '24px',
      border: `1px solid ${c.border}`, marginBottom: '24px' }}>
      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '600',
        color: c.text, margin: '0 0 8px' }}>
        {t('charts.boxplot.title', { pm: 'PM2.5 / PM10' })}
      </h3>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: c.textSec, margin: 0 }}>
        {t('charts.boxplot.needDays')}
        {nbDays > 0 && ' ' + t('charts.boxplot.needDaysCount', { count: nbDays })}
      </p>
    </div>
  );

  // PM10 jamais mesuré (ex. Atmotube Pro 2, D194) : ni boîte, ni légende, ni seuil PM10.
  const hasPm10 = pm10Data.some(day => day.length > 0);

  const makeDataset = (label, data, bg, border) => ({
    label, data,
    backgroundColor: bg, borderColor: border,
    borderWidth: 1.5, outlierColor: border, medianColor: '#fff',
    itemRadius: 2, outlierRadius: 3, padding: 8,
  });

  const chartData = {
    labels,
    datasets: [
      makeDataset('PM2.5 (µg/m³)', pm25Data, 'rgba(139,92,246,0.55)', 'rgba(139,92,246,1)'),
      ...(hasPm10 ? [makeDataset('PM10 (µg/m³)', pm10Data, 'rgba(236,72,153,0.45)', 'rgba(236,72,153,1)')] : []),
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { bottom: 8 } },
    plugins: {
      legend: {
        labels: { color: c.text, font: { family: 'Inter', size: 11 }, padding: 16 }
      },
      tooltip: {
        callbacks: {
          label: ctx => {
            const { min, q1, median, q3, max } = ctx.raw || {};
            if (median == null) return '';
            return [
              ` ${ctx.dataset.label}`,
              `  ${t('charts.boxplot.tipMedian')} : ${median.toFixed(1)} µg/m³`,
              `  Q1-Q3  : ${q1?.toFixed(1)} - ${q3?.toFixed(1)}`,
              `  Min-Max : ${min?.toFixed(1)} - ${max?.toFixed(1)}`,
            ];
          }
        }
      },
      annotation: {
        annotations: {
          seuil25: {
            type: 'line', yMin: 25, yMax: 25,
            borderColor: 'rgba(245,158,11,0.7)', borderWidth: 1.5, borderDash: [4, 4],
            label: { content: t('charts.boxplot.threshold', { pm: 'PM2.5', v: 25 }), display: true, position: 'end',
              color: 'rgba(245,158,11,0.9)', font: { size: 9 } }
          },
          ...(hasPm10 && { seuil50: {
            type: 'line', yMin: 50, yMax: 50,
            borderColor: 'rgba(239,68,68,0.7)', borderWidth: 1.5, borderDash: [4, 4],
            label: { content: t('charts.boxplot.threshold', { pm: 'PM10', v: 50 }), display: true, position: 'end',
              color: 'rgba(239,68,68,0.9)', font: { size: 9 } }
          } }),
        }
      },
      zoom: {
        pan:  { enabled: true, mode: 'x', modifierKey: 'shift' },
        zoom: { wheel: { enabled: true, speed: 0.1 }, pinch: { enabled: true }, mode: 'x' },
        limits: { x: { min: 'original', max: 'original' } }
      },
    },
    scales: {
      x: {
        ticks: { color: c.textSec, font: { family: 'Inter', size: 11 } },
        grid:  { color: c.grid },
        title: { display: true, text: t('charts.boxplot.axis.day'), color: c.textSec, font: { size: 10 } }
      },
      y: {
        ticks: { color: c.textSec, font: { family: 'Inter', size: 10 }, callback: v => `${v} ` },
        grid:  { color: c.grid },
        title: { display: true, text: 'µg/m³', color: c.textSec, font: { size: 10 } }
      }
    }
  };

  const btnStyle = {
    padding: '5px 12px', background: '#1B77F1', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer', marginBottom: '8px'
  };

  return (
    <div style={{
      background: c.card, borderRadius: '16px', padding: '24px',
      border: `1px solid ${c.border}`,
      boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.06)',
      marginBottom: '20px'
    }}>
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: '700', color: BRAND.blueText, margin: 0 }}>
          {t('charts.boxplot.title', { pm: hasPm10 ? 'PM2.5 / PM10' : 'PM2.5' })}
        </h3>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: c.textSec, margin: '4px 0 0' }}>
          {t('charts.boxplot.subtitle', { count: nbDays })}
        </p>
      </div>

      <button type="button" style={btnStyle}
        onClick={() => { if (chartRef.current) chartRef.current.resetZoom(); }}>
        {t('common.zoom.reset')}
      </button>

      <div style={{ height: '300px', marginBottom: '12px' }}>
        <Chart ref={chartRef} type="boxplot" data={chartData} options={options} />
      </div>

      {/* Instructions zoom */}
      <div style={{
        marginTop: '12px', padding: '8px 12px',
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(27, 119, 241, 0.05)',
        borderRadius: '6px',
        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(27, 119, 241, 0.15)',
        fontSize: '13px', color: darkMode ? '#E8EDF2' : '#4A5568',
        textAlign: 'center', lineHeight: '1.5'
      }}>
        <span style={{ display: 'none' }} className="mobile-only">
          💡 {t('common.zoom.touch')}
        </span>
        <span className="desktop-only">
          💡 {t('common.zoom.desktop')}
        </span>
      </div>

      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '10px', color: c.textSec, margin: '8px 0 0', fontStyle: 'italic' }}>
        {t('charts.boxplot.legend')}
      </p>
    </div>
  );
};

export default BoxplotChart;
